import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/app/admin/_common/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/app/admin/_common/ui/sheet";
import CuoButton from "@/app/admin/_common/CuoButton";
import type { FormRequest } from "@/app/_services/contactService";
import { FORM_TYPE_LABELS } from "./requestLabels";
import { Inbox, X, Check } from "lucide-react";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <p className="whitespace-pre-wrap rounded-md border border-border px-3 py-2 text-[13px] text-foreground">
        {value}
      </p>
    </div>
  );
}

export default function RequestViewDialog({
  request,
  onClose,
  onUpdateStatus,
}: {
  request: FormRequest | null;
  onClose: () => void;
  onUpdateStatus: (request: FormRequest, status: FormRequest["status"]) => void;
}) {
  const isMobile = useIsMobile();
  const title = request?.name || "Request";
  const subtitle = request ? FORM_TYPE_LABELS[request.form_type] ?? request.form_type : "";

  const fields = request && (
    <div className="flex flex-col gap-4 py-1">
      <Field label="Form Type" value={subtitle} />
      <Field label="Email" value={request.email || "-"} />
      <Field label="Phone" value={request.phone_number || "-"} />
      <Field label="Comment" value={request.comment || "-"} />
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={request !== null} onOpenChange={(next) => !next && onClose()}>
        <SheetContent
          side="right"
          hideClose
          className="flex h-full w-screen max-w-none flex-col gap-0 border-l-0 bg-white p-0 sm:max-w-none"
        >
          <SheetTitle className="sr-only">{title}</SheetTitle>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="inline-flex items-center gap-2 text-[13px] font-normal text-foreground">
              <Inbox className="size-4 flex-none text-muted-foreground" />
              {title}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
              aria-label="Close"
            >
              <X className="size-3.5" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
            {fields}
          </div>

          {request && (
            <div className="flex items-center justify-end gap-2 border-t border-border p-3">
              <CuoButton
                variant="outline"
                label="Decline"
                icon={<X className="size-3.5" />}
                disabled={request.status === "declined"}
                onClick={() => onUpdateStatus(request, "declined")}
              />
              <CuoButton
                variant="default"
                label="Accept"
                icon={<Check className="size-3.5" />}
                disabled={request.status === "accepted"}
                onClick={() => onUpdateStatus(request, "accepted")}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={request !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="inset-0 left-0 top-0 m-auto h-fit max-h-[80vh] w-full max-w-[480px] translate-x-0 translate-y-0 bg-background">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <Inbox className="size-4 flex-none text-muted-foreground" />
              {title}
            </span>
          </DialogTitle>
        </DialogHeader>

        {fields}
      </DialogContent>
    </Dialog>
  );
}
